import React, { useEffect, useState } from "react";
import axios from "axios";
import { Modal } from "react-bootstrap";
import { Form, Field } from "react-final-form";

interface RegistrationModalProps {
    modalIsOpen: boolean;
    closeModal: () => void;
    onUserCreated?: () => void;
}

interface CountryCode {
    name: string;
    dial_code: string;
    code: string;
}

const UserDetailsModal: React.FC<RegistrationModalProps> = ({
    modalIsOpen,
    closeModal,
    onUserCreated,
}) => {
    const [callingCode, setCallingCode] = useState(null);
    const [countryCodes, setCountryCodes] = useState<CountryCode[]>([]);

    const onUserSubmit = async (values: any) => {
        const res = await axios.post(process.env.REACT_APP_API_URL + "user", {
            ext: values.ext,
            email: values.email,
            phone: values.phone,
            name: values.name,
        });
        localStorage.setItem("token", res.data.token);
        if (onUserCreated) {
            onUserCreated();
        }
        closeModal();
    };

    const userValidate = (values: any) => {
        const errors: any = {};

        // Required fields validation
        ["name", "ext", "phone", "email"].forEach((field) => {
            if (!values[field]) errors[field] = "Required";
        });

        // Additional validations
        if (values.phone && !/^\d{6,15}$/.test(values.phone)) {
            errors.phone = "Invalid phone number";
        }
        if (
            values.email &&
            !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)
        ) {
            errors.email = "Invalid email address";
        }

        return errors;
    };

    useEffect(() => {
        const fetchCountryCodes = async () => {
            try {
                const response = await axios.get("/assets/countrycodes.json");

                // Deduplicate and map flag emojis
                const uniqueCountryCodes = Array.from(
                    new Set(response.data.map((code: any) => code.dial_code))
                ).map((dial_code) => {
                    const country = response.data.find(
                        (code: any) => code.dial_code === dial_code
                    );

                    // Add flag emoji based on country code
                    const code = country.code.toUpperCase();
                    const flag = String.fromCodePoint(
                        ...[...code].map((c) => c.charCodeAt(0) + 0x1f1e6 - 65)
                    );

                    return { ...country, flag };
                });

                // Sort the country codes
                const sortedCountryCodes = uniqueCountryCodes.sort((a, b) => {
                    return a.dial_code.localeCompare(b.dial_code, undefined, {
                        numeric: true,
                    });
                });

                setCountryCodes(sortedCountryCodes);

                // Fetch user location
                const locationResponse = await axios.get(
                    "http://ip-api.com/json"
                );
                const countryCode = locationResponse.data.countryCode;

                // Match user's country code
                const matchedCountry = sortedCountryCodes.find(
                    (code) => code.code === countryCode
                );

                if (matchedCountry) {
                    console.log("Default Dial Code:", matchedCountry.dial_code);
                    setCallingCode(matchedCountry.dial_code);
                    // setDefaultCallingCode(matchedCountry.dial_code); // Set the default calling code
                }
            } catch (error) {
                console.error("Error fetching country codes", error);
            }
        };

        fetchCountryCodes();
    }, []);

    return (
        <div
            className="modal show"
            style={{ display: "block", position: "initial" }}
        >
            <Modal
                show={modalIsOpen}
                onHide={() => {
                    closeModal();
                }}
            >
                <Modal.Header
                    closeButton
                    className="modal-bg"
                    style={{ border: "none", fontFamily: "Noto Sans" }}
                >
                    <Modal.Title>Let's get to know you</Modal.Title>
                </Modal.Header>
                <div
                    className="modal-bg"
                    style={{
                        fontSize: "12px",
                        paddingLeft: "17px",
                        paddingRight: "17px",
                        marginTop: "-12px",
                        fontFamily: "Noto Sans",
                    }}
                >
                    We’ll share cool sustainability tips, your impact report and
                    stories!
                </div>
                <Form
                    onSubmit={onUserSubmit}
                    validate={userValidate}
                    initialValues={{
                        ext: callingCode || "+91",
                    }}
                    render={({ handleSubmit, submitting, pristine, form }) => (
                        <>
                            <Modal.Body
                                className="modal-bg"
                                style={{ border: "none" }}
                            >
                                <form
                                    onSubmit={handleSubmit}
                                    autoComplete="new-password"
                                >
                                    <Field
                                        component="input"
                                        name="name"
                                        type="text"
                                        className="form-control input-control"
                                        placeholder="First Name & Last Name"
                                        autoComplete="new-password" // Use non-standard value
                                    />
                                    <Field
                                        name="name"
                                        render={({ meta }) => (
                                            <div className="error">
                                                {meta.error && meta.touched ? (
                                                    <span>{meta.error}</span>
                                                ) : null}
                                            </div>
                                        )}
                                    />
                                    <br />
                                    <div className="row">
                                        <div className="col-4">
                                            <Field
                                                name="ext"
                                                component="select"
                                                className="form-control input-control input-control-arrow"
                                            >
                                                {countryCodes.map(
                                                    (code: any) => (
                                                        <option
                                                            key={code.code}
                                                            value={
                                                                code.dial_code
                                                            }
                                                        >
                                                            {code.dial_code}{" "}
                                                            {code.flag}
                                                        </option>
                                                    )
                                                )}
                                            </Field>
                                            <Field
                                                name="ext"
                                                render={({ meta }) => (
                                                    <div className="error">
                                                        {meta.error &&
                                                        meta.touched ? (
                                                            <span>
                                                                {meta.error}
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                )}
                                            />
                                        </div>
                                        <div className="col-8">
                                            <Field
                                                component="input"
                                                name="phone"
                                                type="number"
                                                onKeyDown={(e: any) => {
                                                    if (e.key === "e") {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                className="form-control input-control"
                                                placeholder="Mobile Number"
                                                autoComplete="new-password" // Use non-standard value
                                            />
                                            <Field
                                                name="phone"
                                                render={({ meta }) => (
                                                    <div className="error">
                                                        {meta.error &&
                                                        meta.touched ? (
                                                            <span>
                                                                {meta.error}
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                )}
                                            />
                                        </div>
                                        <div className="whatsapp-info">
                                            <div className="first-line">
                                                <span className="italic-text">
                                                    <i>Make sure</i>
                                                </span>
                                                <img
                                                    src="assets/images/icons/whatsapp.svg"
                                                    alt="WhatsApp Logo"
                                                    className="whatsapp-logo-inline"
                                                />
                                                <span className="italic-text">
                                                    <i>
                                                        WhatsApp is enabled on
                                                        this number to
                                                    </i>
                                                </span>
                                            </div>
                                            <div className="second-line">
                                                <span className="italic-text">
                                                    <i>
                                                        receive Impact Reports
                                                    </i>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <br />
                                    <Field
                                        component="input"
                                        name="email"
                                        type="text"
                                        className="form-control input-control"
                                        placeholder="Email"
                                        autoComplete="new-password" // Use non-standard value
                                    />
                                    <Field
                                        name="email"
                                        render={({ meta }) => (
                                            <div className="error">
                                                {meta.error && meta.touched ? (
                                                    <span>{meta.error}</span>
                                                ) : null}
                                            </div>
                                        )}
                                    />
                                </form>
                            </Modal.Body>
                            <Modal.Footer
                                style={{
                                    border: "none",
                                }}
                                className="modal-bg"
                            >
                                <div
                                    style={{
                                        fontSize: "12px",
                                        width: "90%",
                                        textAlign: "center",
                                        fontFamily: "Noto Sans",
                                    }}
                                >
                                    By submitting your response, you agree to
                                    our{" "}
                                    <a href="terms.pdf" target="_blank">
                                        Terms & Conditions
                                    </a>
                                </div>
                                <br />

                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        justifyContent: "space-between",
                                        flex: 1,
                                        fontSize: "13px",
                                    }}
                                >
                                    <div
                                        style={{
                                            fontFamily: "Noto Sans",
                                        }}
                                    >
                                        <span>
                                            <b>Please verify your details </b>{" "}
                                            <br /> Name:{" "}
                                            {form.getState().values.name}
                                        </span>
                                        <br />
                                        <div
                                            style={{
                                                display: "flex",
                                            }}
                                        >
                                            <span>Phone No: </span>
                                            <span>
                                                <img
                                                    // style={{
                                                    //     marginTop: "3px",
                                                    // }}
                                                    src="assets/images/icons/whatsapp.svg"
                                                    alt="WhatsApp Logo"
                                                    className="whatsapp-logo-inline"
                                                />
                                            </span>
                                            {form.getState().values.ext} -{" "}
                                            {form.getState().values.phone}
                                        </div>
                                        Email: {form.getState().values.email}
                                    </div>
                                    <div className="buttons">
                                        <a
                                            style={{
                                                alignItems: "center",
                                                textDecoration: "none",
                                                display: "flex",
                                                flexDirection: "column",
                                                marginRight: "20px",
                                            }}
                                            href=""
                                            onClick={(e) => {
                                                e.preventDefault();
                                                form.submit();
                                            }}
                                        >
                                            <img
                                                style={{
                                                    width: "35px",
                                                }}
                                                src="assets/images/icons/submit.svg"
                                                alt=""
                                            />
                                            <span
                                                style={{
                                                    fontSize: 15,
                                                    color: "#3E3E3E",
                                                    fontWeight: "bold",
                                                    fontFamily: "Noto Sans",
                                                }}
                                            >
                                                Submit
                                            </span>
                                        </a>
                                    </div>
                                </div>
                            </Modal.Footer>
                        </>
                    )}
                />
            </Modal>
        </div>
    );
};

export default UserDetailsModal;
